import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-alpha-common';
/**
 * Ensures that the given item inside a horizontally scrollable container
 * is brought fully into view, while also revealing as much as possible
 * of its immediate sibling (previous or next <li> element).
 *
 * Behavior:
 * - If the item overflows on the left → scroll left.
 *   If a previous sibling exists, try to show it too without hiding the item.
 * - If the item overflows on the right → scroll right.
 *   If a next sibling exists, try to show it too without hiding the item.
 * - Always ensures the clicked item remains fully visible in the container.
 */
/**
 * Considerations for future improvements:
 * - Currently the scrolling correction only happens when an active item overflows left or right.
 *   Nothing happens if an item is basically on the edge of the container but not overflowing.
 *   This way, the next or previous sibling is not shown.
 */
export const handleScrollInView = ({ containerRef, itemRef }) => {
    const container = containerRef.current;
    const item = itemRef.current;
    if (!container || !item)
        return;
    // Bounding boxes for container and clicked item
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    // Calculate overflows:
    // - Negative leftOverflow → item is clipped on the left
    // - Positive rightOverflow → item is clipped on the right
    const leftOverflow = itemRect.left - containerRect.left;
    const rightOverflow = itemRect.right - containerRect.right;
    // Items are assumed to be inside <li> tags (horizontal list)
    const parentLi = item.closest('li');
    if (!parentLi)
        return;
    // Horizontal gap between list items (from design tokens)
    const gap = parseInt(SYS_SPACE_FIXED_100);
    // Case 1: item is cut off on the LEFT
    if (leftOverflow < 0) {
        const prevLi = parentLi.previousElementSibling;
        if (prevLi) {
            const prevLiRect = prevLi.getBoundingClientRect();
            // Default: scroll so that prevLi aligns with container left
            let delta = prevLiRect.left - containerRect.left;
            // If prevLi + gap + item are wider than container,
            // scrolling prevLi fully into view would push item out.
            // → Clamp so item stays fully visible on the right.
            if (prevLiRect.width + gap + itemRect.width > containerRect.width) {
                const itemRightEdge = itemRect.right - containerRect.right;
                delta = Math.max(delta, itemRightEdge);
            }
            return container.scrollBy({ left: delta, behavior: 'smooth' });
        }
        // No previous sibling → just align item itself to the left
        const delta = itemRect.left - containerRect.left;
        container.scrollBy({ left: delta, behavior: 'smooth' });
        // Case 2: item is cut off on the RIGHT
    }
    else if (rightOverflow > 0) {
        const nextLi = parentLi.nextElementSibling;
        if (nextLi) {
            const nextLiRect = nextLi.getBoundingClientRect();
            // Default: scroll so that nextLi aligns with container right
            let delta = nextLiRect.right - containerRect.right;
            // If nextLi + gap + item are wider than container,
            // scrolling nextLi fully into view would push item out.
            // → Clamp so item stays fully visible on the left.
            if (nextLiRect.width + gap + itemRect.width > containerRect.width) {
                const itemLeftEdge = itemRect.left - containerRect.left;
                delta = Math.min(delta, itemLeftEdge);
            }
            return container.scrollBy({ left: delta, behavior: 'smooth' });
        }
        else {
            // No next sibling → just align item itself to the right
            const delta = itemRect.right - containerRect.right;
            return container.scrollBy({ left: delta, behavior: 'smooth' });
        }
    }
};
//# sourceMappingURL=handle-scroll-in-view.js.map